import { supabase } from "@project/lib/data-client";
import { z } from "zod";

async function requireUserId() {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  if (error || !user) {
    throw new Error("Sign in required");
  }
  return user.id;
}

export async function linkAndGetDonor(_args?: unknown) {
  const userId = await requireUserId();
  const { data: linked } = await supabase
    .from("donors")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();
  if (linked) {return { donor: linked };}

  const { data: auth } = await supabase.auth.getUser();
  const email = auth.user?.email ?? null;

  if (email) {
    const { data: byEmail } = await supabase
      .from("donors")
      .select("*")
      .ilike("email", email)
      .is("user_id", null)
      .maybeSingle();
    if (byEmail) {
      await supabase
        .from("donors")
        .update({ user_id: userId })
        .eq("id", byEmail.id);
      return { donor: { ...byEmail, user_id: userId } };
    }
  }
  return { donor: null };
}

export async function getDonorOverview(_args?: unknown) {
  const userId = await requireUserId();
  const { data: donor } = await supabase
    .from("donors")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();
  if (!donor) {
    return { donor: null, stats: null, donations: [], recurring: [] };
  }

  const [{ data: donations }, { data: recurring }] = await Promise.all([
    supabase
      .from("donations")
      .select(
        "id,receipt_no,amount,donation_date,payment_status,payment_mode,category,purpose,requires_80g,transaction_id,frequency,created_at,donor:donors(full_name,mobile,email,address,city,state,pincode,pan)"
      )
      .eq("donor_id", donor.id)
      .order("created_at", { ascending: false }),
    supabase
      .from("recurring_donations")
      .select("*")
      .eq("donor_id", donor.id)
      .order("created_at", { ascending: false }),
  ]);

  const ok = (donations ?? []).filter(
    (d: { payment_status?: string | null }) =>
      (d.payment_status ?? "success") === "success"
  );
  const lifetime_total = ok.reduce(
    (sum, d: { amount?: number | null }) => sum + Number(d.amount ?? 0),
    0
  );

  return {
    donor,
    stats: {
      lifetime_total,
      lifetime_count: ok.length,
      fy_total: lifetime_total,
      last_donation_at: ok[0]?.donation_date ?? null,
    },
    donations: (donations ?? []).map((d: { donor?: unknown }) => ({
      ...d,
      donor: Array.isArray(d.donor) ? d.donor[0] : d.donor,
    })),
    recurring: recurring ?? [],
  };
}

export async function updateDonorProfile({
  data,
}: {
  data: {
    full_name: string;
    mobile: string;
    email?: string | null;
    pan?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    pincode?: string | null;
    country?: string | null;
    date_of_birth?: string | null;
  };
}) {
  const parsed = z
    .object({
      full_name: z.string().trim().min(2).max(120),
      mobile: z.string().trim().min(7).max(20),
      email: z.string().email().max(120).optional().nullable(),
      pan: z
        .string()
        .trim()
        .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/i)
        .optional()
        .nullable()
        .or(z.literal("")),
      address: z.string().max(300).optional().nullable(),
      city: z.string().max(80).optional().nullable(),
      state: z.string().max(80).optional().nullable(),
      pincode: z.string().max(20).optional().nullable(),
      country: z.string().max(80).optional().nullable(),
      date_of_birth: z.string().optional().nullable(),
    })
    .parse(data);

  const userId = await requireUserId();
  const { data: donor } = await supabase
    .from("donors")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();
  if (!donor) {throw new Error("No donor profile linked");}

  const payload = {
    full_name: parsed.full_name,
    mobile: parsed.mobile,
    email: parsed.email ?? null,
    pan: (parsed.pan ?? "").toUpperCase() || null,
    address: parsed.address ?? null,
    city: parsed.city ?? null,
    state: parsed.state ?? null,
    pincode: parsed.pincode ?? null,
    country: parsed.country ?? "India",
    date_of_birth: parsed.date_of_birth ?? null,
  };
  await supabase.from("donors").update(payload).eq("id", donor.id);
  return { ok: true };
}

export async function setRecurringStatus({
  data,
}: {
  data: { id: string; status: "active" | "paused" | "cancelled" };
}) {
  const parsed = z
    .object({
      id: z.string().uuid(),
      status: z.enum(["active", "paused", "cancelled"]),
    })
    .parse(data);

  const userId = await requireUserId();
  const { data: donor } = await supabase
    .from("donors")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();
  if (!donor) {throw new Error("No donor profile linked");}

  const { data: row } = await supabase
    .from("recurring_donations")
    .select("id, donor_id")
    .eq("id", parsed.id)
    .maybeSingle();
  if (!row || row.donor_id !== donor.id) {
    throw new Error("Not your subscription");
  }

  await supabase
    .from("recurring_donations")
    .update({ status: parsed.status })
    .eq("id", parsed.id);
  return { ok: true };
}
